import { Tooltip } from '../ui/tooltip';
import { IAddressSchema } from '@monetize/types/app';
import lodashGet from 'lodash/get';
import { useMemo } from 'react';
import { Controller, FieldErrors, useFormContext } from 'react-hook-form';
import {
  countriesWithStates,
  getStatesFromCountry,
} from '../../services/countryState';
import { MFormField, MGrid, MGridItem, MInput } from '../Monetize';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';
import { MainSearchInputV2 } from '../Monetize/MCustomSelect/components/MainSearchInputV2';

/**
 * Shape of the address form data
 * We can add new shapes to this as needed, and formKeyPrefix also needs
 * to be updated accordingly
 */
type AddressFormShape =
  | IAddressSchema
  | { address: IAddressSchema }
  | { billingAddress: IAddressSchema; shippingAddress: IAddressSchema }
  | {
      contacts: {
        billingContact: { address: IAddressSchema };
        shippingContact: { address: IAddressSchema };
      };
    }
  | {
      billingContact: { address: IAddressSchema };
      shippingContact: { address: IAddressSchema };
    };

interface AddressFormProps {
  formKeyPrefix:
    | ''
    | 'address.'
    | 'billingAddress.'
    | 'shippingAddress.'
    | 'contacts.billingContact.address.'
    | 'contacts.shippingContact.address.'
    | 'billingContact.address.'
    | 'shippingContact.address.';
  isDisabled?: boolean;
  isReadOnly?: boolean;
  requireAddress?: boolean;
}

function getError(
  errors: FieldErrors<AddressFormShape>,
  formKeyPrefix: AddressFormProps['formKeyPrefix'],
  field: keyof IAddressSchema,
) {
  return lodashGet(errors, `${formKeyPrefix}${field}`);
}

/**
 * AddressForm component
 *
 * This relies on <FormProvider> being used in the parent component so that
 * we can handle dynamic data shapes
 */
export function AddressForm({
  formKeyPrefix,
  isDisabled = false,
  isReadOnly = false,
  requireAddress = false,
}: AddressFormProps) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<AddressFormShape>();

  const readOnlyProps = {
    isDisabled: isReadOnly || isDisabled,
    isReadOnly,
    variant: isReadOnly ? 'readonly' : 'outline',
  } as const;

  const selectedCountry = watch(`${formKeyPrefix}country`);

  const { stateFormat, states } = useMemo(() => {
    const _states = getStatesFromCountry(selectedCountry);
    return {
      states: _states,
      stateFormat: _states?.length > 0 ? 'select' : 'input',
    };
  }, [selectedCountry]);

  return (
    <MGrid templateColumns="repeat(12, 1fr)" gap={4}>
      <MGridItem colSpan={12}>
        <MFormField
          error={getError(errors, formKeyPrefix, 'country')}
          label="Country"
          isRequired={requireAddress}
        >
          <Controller
            name={`${formKeyPrefix}country`}
            control={control}
            defaultValue=""
            render={({ field: { value, onChange, ...rest } }) => (
              <MCustomSelect
                useMainInputAsSearchInput
                MainInputComponent={MainSearchInputV2}
                useHighlightedItemList
                items={countriesWithStates}
                itemTitle="name"
                itemValue="isoCode"
                value={value || ''}
                clearable
                onChange={(ev) => {
                  onChange(ev);
                }}
                useRawValue
                {...rest}
                {...readOnlyProps}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 12, 12, 6]}>
        <MFormField
          error={getError(errors, formKeyPrefix, 'line1')}
          label="Address Line 1"
          isRequired={requireAddress}
        >
          <Controller
            name={`${formKeyPrefix}line1`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) => (
              <MInput
                value={value || ''}
                {...rest}
                {...readOnlyProps}
                maxLength={255}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 12, 12, 6]}>
        <MFormField
          error={getError(errors, formKeyPrefix, 'line2')}
          label="Address Line 2"
        >
          <Controller
            name={`${formKeyPrefix}line2`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) => (
              <MInput
                value={value || ''}
                {...rest}
                {...readOnlyProps}
                maxLength={255}
              />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 12, 4]}>
        <MFormField
          error={getError(errors, formKeyPrefix, 'city')}
          label="City"
          isRequired={requireAddress}
        >
          <Controller
            name={`${formKeyPrefix}city`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) => (
              <MInput value={value || ''} {...rest} {...readOnlyProps} />
            )}
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 12, 4]}>
        <MFormField
          error={getError(errors, formKeyPrefix, 'state')}
          label="State/Province"
          isRequired={requireAddress && states.length > 0}
        >
          <Controller
            name={`${formKeyPrefix}state`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) =>
              stateFormat === 'select' ? (
                <MCustomSelect
                  useMainInputAsSearchInput
                  MainInputComponent={MainSearchInputV2}
                  useHighlightedItemList
                  items={states}
                  itemTitle="name"
                  itemValue="isoCode"
                  value={value || ''}
                  clearable
                  useRawValue
                  {...rest}
                  {...readOnlyProps}
                />
              ) : (
                <Tooltip
                  disabled={!!selectedCountry}
                  content="Choose a country to see available states"
                >
                  <MInput
                    value={value || ''}
                    {...rest}
                    {...readOnlyProps}
                    isDisabled={!selectedCountry}
                    placeholder={
                      !selectedCountry
                        ? 'Select country first'
                        : 'Enter state/province'
                    }
                  />
                </Tooltip>
              )
            }
          />
        </MFormField>
      </MGridItem>

      <MGridItem colSpan={[12, 12, 4]}>
        <MFormField
          error={getError(errors, formKeyPrefix, 'postalCode')}
          label="Postal Code"
          isRequired={requireAddress}
        >
          <Controller
            name={`${formKeyPrefix}postalCode`}
            control={control}
            defaultValue=""
            render={({ field: { value, ...rest } }) => (
              <MInput value={value || ''} {...rest} {...readOnlyProps} />
            )}
          />
        </MFormField>
      </MGridItem>
    </MGrid>
  );
}
